import { CohortCellKind, CohortCellRef, CohortCellSource, CohortInputRule, CohortSourceTerm, parseCohortYm } from './cohort-forecast';
import { GUARD_STRUCTURE, METHOD_CONSTANTS, METHOD_CONSTANT_LIMITS, MethodConstantKey } from './cohort-guard-defaults';

export type GuardId = 'legacy' | 'credible' | 'anchors' | 'trend' | 'tail' | 'cap' | 'qa' | 'retention' | 'own';
export type GuardParam =
  | 'legacyCutoff' | 'legacyRetention' | 'legacyCeiling'
  | 'minSize' | 'k'
  | 'trendWindow' | 'trendBands' | 'trendLimits' | 'trendApplications'
  | 'tailWindow' | 'rateCap' | 'qaMultiple'
  | 'retentionChange' | 'retentionChangeStart' | 'ownHistoryOn' | 'ownHistoryMinMonths' | 'bundleRetentionWindow' | 'ceilingHeadroom';
export type GuardParamValue = number | Array<number | null> | null;

export const COHORT_GUARD_ENGINE_VERSION = '0.7.0';
export const FLAT_TAIL_ENGINE_VERSION = '0.2.0';
const FADE_ENGINE_VERSION = '0.3.0';
const AGE_FADE_ENGINE_VERSION = '0.4.0';
const TAIL_EVIDENCE_ENGINE_VERSION = '0.5.0';
const TREND_YEARS_ENGINE_VERSION = '0.5.0';
export const RETENTION_ENGINE_VERSION = '0.6.0';
export const SEASON_RATIO_ENGINE_VERSION = '0.7.0';
export const TREND_WINDOW_CHOICES = [12, 24];
export const OWN_HISTORY_MONTHS_RANGE: [number, number] = [2, 12];
export const BUNDLE_WINDOW_RANGE: [number, number] = [3, 12];
const ENGINE_PATTERN = /^\d{1,4}\.\d{1,4}\.\d{1,4}$/;
export const GUARD_IDS: GuardId[] = ['legacy', 'credible', 'anchors', 'trend', 'tail', 'cap', 'qa', 'retention', 'own'];
export const GUARD_PARAMS: GuardParam[] = [
  'legacyCutoff', 'legacyRetention', 'legacyCeiling', 'minSize', 'k',
  'trendWindow', 'trendBands', 'trendLimits', 'trendApplications', 'tailWindow', 'rateCap', 'qaMultiple',
  'retentionChange', 'retentionChangeStart', 'ownHistoryOn', 'ownHistoryMinMonths', 'bundleRetentionWindow', 'ceilingHeadroom'
];
export const TREND_PARAMS: GuardParam[] = ['trendWindow', 'trendBands', 'trendLimits', 'trendApplications'];
export const RETENTION_PARAMS: GuardParam[] = ['retentionChange', 'retentionChangeStart', 'ownHistoryOn', 'ownHistoryMinMonths', 'bundleRetentionWindow', 'ceilingHeadroom'];
export const TUNED_PARAMS: GuardParam[] = ['bundleRetentionWindow', 'ownHistoryMinMonths', 'ownHistoryOn'];
export const GUARD_OF_PARAM: Record<GuardParam, GuardId> = {
  legacyCutoff: 'legacy', legacyRetention: 'legacy', legacyCeiling: 'legacy',
  minSize: 'credible', k: 'credible',
  trendWindow: 'trend', trendBands: 'trend', trendLimits: 'trend', trendApplications: 'trend',
  tailWindow: 'tail', rateCap: 'cap', qaMultiple: 'qa',
  retentionChange: 'retention', retentionChangeStart: 'retention',
  ownHistoryOn: 'own', ownHistoryMinMonths: 'own',
  bundleRetentionWindow: 'legacy', ceilingHeadroom: 'cap'
};

export type CohortMethodUse = 'own' | 'tab' | 'user';

export interface CohortTuned {
  ownHistoryOn?: number | null;
  ownHistoryMinMonths?: number | null;
  bundleRetentionWindow?: number | null;
  error?: number | null;
  cut?: number;
  at?: string;
  through?: number;
  window?: number;
  changedAt?: string;
}

export interface GuardUserValue {
  value: GuardParamValue;
  by?: string;
  at?: string;
}

export interface CohortGuardScope {
  off?: GuardId[];
  user?: Partial<Record<GuardParam, GuardUserValue>>;
  system?: Partial<Record<GuardParam, GuardParamValue>>;
  tuned?: CohortTuned;
}

export interface CohortGuardSettings {
  scopes?: Record<string, CohortGuardScope>;
  constants?: Partial<Record<MethodConstantKey, number>>;
  engine?: string;
}

export interface GuardParamInfo {
  value: GuardParamValue;
  source: 'user' | 'system';
  reason: string;
  system: GuardParamValue;
  systemReason: string;
  by?: string;
  at?: string;
}

export interface ResolvedGuards {
  on: Record<GuardId, boolean>;
  off: Record<GuardId, boolean>;
  needs: GuardParam[];
  params: Record<GuardParam, GuardParamInfo>;
  trendUsed: boolean;
  seasonUsed: boolean;
  monthFactors: number[];
  monthYears: number[];
  factorsMeasured: boolean;
  v4: boolean;
  capSeason: boolean;
  trendYear: number | null;
  trendHeld: boolean;
  bands: number[];
  limits: number[];
  caps: Array<number | null>;
  flatTail: boolean;
  tailRate: number | null;
  tailAges: number[];
  fade: number | null;
  fadeFrom: 'curve' | 'bundle' | null;
  fadeAges: number[];
  fadeNeeds: boolean;
  v3: boolean;
  retention: { value: number; start: number | null };
  signal: RetentionSignal[];
  ownOn: boolean;
  ownMinMonths: number | null;
  ownChangedAt: string | null;
  headroom: number[];
  capFrom: Array<CapSource | null>;
  bundle: BundleRetention | null;
  bundleNeeds: boolean;
  tunedError: number | null;
}

export interface RetentionSignal {
  change: number | null;
  pairs: number;
}

export interface CapSource {
  j: number;
  t: number;
  rate: number;
}

export interface BundleRetention {
  window: number;
  months: number[];
  now: number;
  before: number;
  yearly: number;
  kept: number;
}

export interface OwnHistorySource {
  age: number;
  anchor: number;
  anchorMonths: Array<{ t: number; rate: number; factor: number; level: number }>;
  decays: Array<{ age: number; value: number; cohorts: number; fromFade: boolean }>;
  factor: number;
}

export interface GuardNote {
  guard: GuardId;
  param?: GuardParam;
  value?: GuardParamValue;
  before?: number | null;
  after?: number | null;
  detail?: string;
}

export interface QaFlag {
  rule: 'R1' | 'R2' | 'R3';
  threshold: number;
  expected: number | null;
}

export interface FadeAge {
  age: number;
  rate: number | null;
  cohorts: number;
  share: number;
  value: number;
}

export interface FadeSource {
  value: number;
  months: number;
  from: 'curve' | 'bundle';
  ages: number[];
  cells: CohortCellRef[];
  pooled?: number;
  age?: FadeAge;
}

export interface GuardedCellSource extends CohortCellSource {
  step?: 'seasonal' | 'prev' | 'average' | 'chain' | 'start' | 'fixed' | 'tail' | 'pooled' | 'curve' | 'own';
  guards?: GuardNote[];
  engineValue?: number;
  fade?: FadeSource;
  base?: number;
  season?: { value: number; month: number; ratio: boolean; measured: boolean; from?: number; takenOut?: boolean };
  carry?: { j: number; t: number; rate: number; before: number; now: number };
  own?: OwnHistorySource;
  change?: { value: number; start: number; before: number };
  cap?: { best: number; j: number; t: number; headroom: number; uncapped: number; factor?: number };
}

export interface LegacySource {
  method: 'actual' | 'seasonal' | 'average';
  from: number | null;
  base: number;
  retention: number;
  power: number;
  result: number;
  change?: number;
}

export interface LegacyBlock {
  on: boolean;
  cutoff: number | null;
  size: number;
  orders: Array<number | null>;
  folded: boolean[];
  sources: Array<LegacySource | null>;
}

export interface GuardedForecastInput {
  months: number[];
  cut: number;
  cohorts: Array<{ label: string | number; ratio?: Array<number | null | undefined> | null }>;
  sizes: Array<number | null | undefined>;
  override?: (j: number, t: number) => number | null | undefined;
  ruleFor: (label: string) => CohortInputRule;
  channelRule: CohortInputRule;
  cohortSet?: (label: string) => boolean;
  scope?: CohortGuardScope | null;
  constants?: Partial<Record<MethodConstantKey, number>> | null;
  engine?: string | null;
  explain?: boolean;
}

export interface GuardedForecastResult {
  rates: Array<Array<number | null>>;
  kinds: Array<Array<CohortCellKind | null>>;
  sources?: Array<Array<GuardedCellSource | null>>;
  guards: ResolvedGuards;
  legacy: LegacyBlock;
  flags: Array<Array<QaFlag[] | null>>;
  flagCount: number;
  changed: Record<GuardId, number>;
  methods: Array<CohortMethodUse | null>;
}

const SEASON = 12;
export const LOCAL_SPAN = 6;
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const pct = (v: number) => String(Math.round(v * 1000) / 10) + '%';
const ymText = (ym: number) => {
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return names[(ym % 100) - 1] + ' ' + String(Math.floor(ym / 100) % 100).padStart(2, '0');
};

export function cohortGuardKey(ch: string, blk: string): string {
  return ch + '|' + blk;
}

export function resolveConstants(raw?: Partial<Record<MethodConstantKey, number>> | null): Record<MethodConstantKey, number> {
  const out = Object.assign({}, METHOD_CONSTANTS);
  (Object.keys(METHOD_CONSTANTS) as MethodConstantKey[]).forEach(key => {
    const v = raw ? raw[key] : undefined;
    const [lo, hi] = METHOD_CONSTANT_LIMITS[key];
    if (isNum(v) && v >= lo && v <= hi) out[key] = v;
  });
  return out;
}

const isMonth = (v: unknown) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= 190001 && n <= 999912 && n % 100 >= 1 && n % 100 <= 12;
};

export function isValidGuardParam(param: GuardParam, v: unknown): v is GuardParamValue {
  if (v === null) return true;
  switch (param) {
    case 'legacyCutoff':
    case 'retentionChangeStart':
      return isMonth(v);
    case 'retentionChange':
      return isNum(v) && v > -1 && v <= 10;
    case 'ownHistoryOn':
      return v === 0 || v === 1;
    case 'ownHistoryMinMonths':
      return Number.isInteger(v) && (v as number) >= OWN_HISTORY_MONTHS_RANGE[0] && (v as number) <= OWN_HISTORY_MONTHS_RANGE[1];
    case 'bundleRetentionWindow':
      return Number.isInteger(v) && (v as number) >= BUNDLE_WINDOW_RANGE[0] && (v as number) <= BUNDLE_WINDOW_RANGE[1];
    case 'ceilingHeadroom':
      return (isNum(v) && v >= 0) || (Array.isArray(v) && v.length > 0 && v.some(isNum) && v.every(x => x === null || (isNum(x) && x >= 0)));
    case 'legacyRetention':
    case 'legacyCeiling':
      return isNum(v) && v > 0 && v <= 2;
    case 'minSize':
      return isNum(v) && v >= 0;
    case 'k':
    case 'trendWindow':
    case 'trendApplications':
    case 'tailWindow':
      return Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 120;
    case 'trendBands':
      return Array.isArray(v) && v.length > 0 && v.every((x, i) => Number.isInteger(x) && x >= 1 && (i === 0 || x > v[i - 1]));
    case 'trendLimits':
      return Array.isArray(v) && v.length > 0 && v.length % 2 === 0 && v.some(isNum)
        && v.every(x => x === null || (isNum(x) && x > 0))
        && v.every((x, i) => i % 2 === 1 || !isNum(x) || !isNum(v[i + 1]) || x <= v[i + 1]);
    case 'rateCap':
      return (isNum(v) && v > 0) || (Array.isArray(v) && v.length > 0 && v.some(isNum) && v.every(x => x === null || (isNum(x) && x > 0)));
    case 'qaMultiple':
      return isNum(v) && v > 0;
    default:
      return false;
  }
}

export function sanitizeCohortGuards(raw: unknown, keepSystem = true): CohortGuardSettings {
  const out: CohortGuardSettings = { scopes: {} };
  if (!raw || typeof raw !== 'object') return out;
  const src = raw as { scopes?: unknown; constants?: unknown };
  if (src.scopes && typeof src.scopes === 'object') {
    Object.keys(src.scopes as object).forEach(key => {
      const parts = key.split('|');
      if (parts.length !== 2 || parts.some(p => !p)) return;
      const scope = (src.scopes as Record<string, any>)[key];
      if (!scope || typeof scope !== 'object') return;
      const clean: CohortGuardScope = {};
      if (keepSystem && Array.isArray(scope.off)) {
        const off = GUARD_IDS.filter(g => scope.off.indexOf(g) >= 0);
        if (off.length) clean.off = off;
      }
      if (scope.user && typeof scope.user === 'object') {
        const user: Partial<Record<GuardParam, GuardUserValue>> = {};
        GUARD_PARAMS.forEach(p => {
          const u = scope.user[p];
          if (u && typeof u === 'object' && 'value' in u && isValidGuardParam(p, u.value)) {
            user[p] = Object.assign({ value: u.value }, typeof u.by === 'string' ? { by: u.by } : null, typeof u.at === 'string' ? { at: u.at } : null);
          }
        });
        if (Object.keys(user).length) clean.user = user;
      }
      if (keepSystem && scope.system && typeof scope.system === 'object') {
        const system: Partial<Record<GuardParam, GuardParamValue>> = {};
        GUARD_PARAMS.forEach(p => {
          if (p in scope.system && isValidGuardParam(p, scope.system[p])) system[p] = scope.system[p];
        });
        if (Object.keys(system).length) clean.system = system;
      }
      const tuned = sanitizeTuned(scope.tuned);
      if (tuned) clean.tuned = tuned;
      out.scopes![key] = clean;
    });
  }
  if (src.constants && typeof src.constants === 'object') {
    const constants: Partial<Record<MethodConstantKey, number>> = {};
    (Object.keys(METHOD_CONSTANTS) as MethodConstantKey[]).forEach(key => {
      const v = (src.constants as Record<string, unknown>)[key];
      const [lo, hi] = METHOD_CONSTANT_LIMITS[key];
      if (isNum(v) && v >= lo && v <= hi) constants[key] = v;
    });
    if (Object.keys(constants).length) out.constants = constants;
  }
  if (keepSystem) {
    const engine = (src as { engine?: unknown }).engine;
    out.engine = typeof engine === 'string' && ENGINE_PATTERN.test(engine) ? engine : FLAT_TAIL_ENGINE_VERSION;
  }
  return out;
}

export function sanitizeTuned(raw: unknown): CohortTuned | null {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as Record<string, unknown>;
  const out: CohortTuned = {};
  TUNED_PARAMS.forEach(p => {
    const v = src[p];
    if (v === null || (v !== undefined && isValidGuardParam(p, v))) (out as Record<string, unknown>)[p] = v;
  });
  if (src.error === null || (isNum(src.error) && src.error >= 0 && src.error <= 10)) out.error = src.error as number | null;
  if (isMonth(src.cut)) out.cut = src.cut as number;
  if (isMonth(src.through)) out.through = src.through as number;
  if (Number.isInteger(src.window) && (src.window as number) >= 1 && (src.window as number) <= 36) out.window = src.window as number;
  if (typeof src.at === 'string' && src.at && !isNaN(Date.parse(src.at))) out.at = src.at;
  if (typeof src.changedAt === 'string' && src.changedAt && !isNaN(Date.parse(src.changedAt))) out.changedAt = src.changedAt;
  return Object.keys(out).length ? out : null;
}

export function usesRetentionChange(engine: string | null | undefined): boolean {
  return !engineBefore(engine, RETENTION_ENGINE_VERSION);
}

export function usesSeasonRatio(engine: string | null | undefined): boolean {
  return !engineBefore(engine, SEASON_RATIO_ENGINE_VERSION);
}

export const STABLE_DROPPED_PARAMS: GuardParam[] = ['ownHistoryOn', 'bundleRetentionWindow'];

export function paramsForEngine(engine: string | null | undefined): GuardParam[] {
  const v3 = usesRetentionChange(engine);
  const v4 = usesSeasonRatio(engine);
  return GUARD_PARAMS.filter(p => (v3 ? TREND_PARAMS.indexOf(p) < 0 : RETENTION_PARAMS.indexOf(p) < 0) && !(v4 && STABLE_DROPPED_PARAMS.indexOf(p) >= 0));
}

export function engineBefore(engine: string | null | undefined, target: string): boolean {
  if (!engine || !ENGINE_PATTERN.test(engine)) return false;
  const a = engine.split('.').map(Number), b = target.split('.').map(Number);
  for (let i = 0; i < 3; i++) if (a[i] !== b[i]) return a[i] < b[i];
  return false;
}

export function usesFlatTail(engine: string | null | undefined): boolean {
  return engineBefore(engine, FADE_ENGINE_VERSION);
}

function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = xs.slice().sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

function percentile(xs: number[], p: number): number | null {
  if (!xs.length) return null;
  const s = xs.slice().sort((a, b) => a - b);
  return s[Math.max(0, Math.min(s.length - 1, Math.ceil((p / 100) * s.length) - 1))];
}

const monthNo = (ym: number) => Math.floor(ym / 100) * 12 + (ym % 100) - 1;

function mergeLimits(own: GuardParamValue, sys: GuardParamValue): GuardParamValue {
  if (!Array.isArray(own)) return own;
  const base = Array.isArray(sys) && sys.length === own.length ? sys : [];
  return own.map((x, i) => (isNum(x) ? x : isNum(base[i]) ? (base[i] as number) : null));
}

export function forecastGuardedCohorts(input: GuardedForecastInput): GuardedForecastResult {
  const { months, cut, cohorts, sizes, override, ruleFor, channelRule, explain } = input;
  const scope = input.scope || {};
  const C = resolveConstants(input.constants);
  const flatTail = engineBefore(input.engine, FADE_ENGINE_VERSION);
  const ageFade = !flatTail && !engineBefore(input.engine, AGE_FADE_ENGINE_VERSION);
  const tailEvidence = !flatTail && !engineBefore(input.engine, TAIL_EVIDENCE_ENGINE_VERSION);
  const v3 = !flatTail && usesRetentionChange(input.engine);
  const v4 = v3 && usesSeasonRatio(input.engine);
  const n = months.length;
  const J = cohorts.length;
  const cutYm = months[Math.max(0, Math.min(cut, n - 1))];
  const rates: Array<Array<number | null>> = cohorts.map(() => new Array(n).fill(null));
  const kinds: Array<Array<CohortCellKind | null>> = cohorts.map(() => new Array(n).fill(null));
  const sources: Array<Array<GuardedCellSource | null>> | undefined = explain ? cohorts.map(() => new Array(n).fill(null)) : undefined;
  const isPre = cohorts.map(c => /^pre/i.test(String(c.label)));
  const ym = cohorts.map((c, j) => (isPre[j] ? NaN : parseCohortYm(c.label)));
  const birth = ym.map(y => (isNaN(y) ? -1 : months.indexOf(y)));
  const sizeOf = (j: number) => (isNum(sizes[j]) ? (sizes[j] as number) : 0);

  for (let j = 0; j < J; j++) {
    const ratio = cohorts[j].ratio || [];
    for (let t = 0; t < n; t++) {
      const ov = override && t > cut ? override(j, t) : undefined;
      if (isNum(ov)) { rates[j][t] = ov; kinds[j][t] = 'override'; continue; }
      if (!isPre[j] && (birth[j] < 0 || t < birth[j])) continue;
      if (t <= cut) {
        const v = ratio[t];
        if (isNum(v)) { rates[j][t] = v; kinds[j][t] = 'actual'; }
      } else if (!isPre[j] && t === birth[j]) {
        rates[j][t] = 1;
        kinds[j][t] = 'birth';
      }
    }
  }

  const month0 = cohorts.map((_, j) => {
    const b = birth[j];
    if (isPre[j] || b < 0 || b > cut || kinds[j][b] !== 'actual') return 0;
    return sizeOf(j) * (rates[j][b] as number);
  });

  const user = scope.user || {};
  const frozen = scope.system || {};
  const params = {} as Record<GuardParam, GuardParamInfo>;
  const trendYears = !flatTail && !engineBefore(input.engine, TREND_YEARS_ENGINE_VERSION);
  const take = (p: GuardParam, derive: () => { value: GuardParamValue; reason: string }) => {
    const u = p === 'trendWindow' && trendYears && user[p] && TREND_WINDOW_CHOICES.indexOf(user[p]!.value as number) < 0 ? undefined : user[p];
    const sys = p in frozen ? { value: frozen[p] as GuardParamValue, reason: 'System default saved with this version' } : derive();
    if (u) {
      const value = p === 'trendLimits' ? mergeLimits(u.value, sys.value) : u.value;
      params[p] = Object.assign({ value, source: 'user' as const, reason: 'Set by user', system: sys.value, systemReason: sys.reason }, u.by ? { by: u.by } : null, u.at ? { at: u.at } : null);
    } else {
      params[p] = { value: sys.value, source: 'system', reason: sys.reason, system: sys.value, systemReason: sys.reason };
    }
    return params[p].value;
  };
  const num = (v: GuardParamValue) => (isNum(v) ? v : null);

  const ruleK = channelRule && channelRule.method !== 'fixed' && channelRule.method !== 'prev' && isNum(channelRule.n) ? channelRule.n : null;
  const k = num(take('k', () => ({ value: ruleK ?? GUARD_STRUCTURE.k, reason: 'Set in the forecast method' }))) || GUARD_STRUCTURE.k;

  const minSize = num(take('minSize', () => {
    let wsum = 0, osum = 0;
    const histFrom = cut - C.matureHistoryMonths;
    for (let j = 0; j < J; j++) {
      if (!month0[j]) continue;
      for (let t = Math.max(birth[j], histFrom + 1); t <= cut; t++) {
        const a = t - birth[j];
        if (a < C.matureAgeFrom || a > C.matureAgeTo || kinds[j][t] !== 'actual') continue;
        osum += month0[j] * (rates[j][t] as number);
        wsum += month0[j];
      }
    }
    const reached = month0.filter((m, j) => m > 0 && cut - birth[j] >= C.matureAgeTo).length;
    if (!wsum || !osum || reached < k) return { value: null, reason: 'Fewer than ' + k + ' cohorts have reached age ' + C.matureAgeTo };
    const mature = osum / wsum;
    const v = Math.ceil(C.evidenceTarget / (mature * k));
    return { value: v, reason: C.evidenceTarget + ' expected repeat orders ÷ (typical repeat rate ' + pct(mature) + ' × ' + k + ' cohorts)' };
  }));

  const ordered = cohorts.map((_, j) => j).filter(j => !isPre[j] && !isNaN(ym[j]) && ym[j] <= cutYm).sort((x, y) => ym[x] - ym[y]);
  const legacyCutoff = num(take('legacyCutoff', () => {
    if (minSize === null) return { value: null, reason: 'Needs the minimum first month orders first' };
    let run = 0, start = -1, prev = NaN;
    for (const j of ordered) {
      const steady = month0[j] >= minSize && (run === 0 || monthNo(ym[j]) === monthNo(prev) + 1);
      if (steady) {
        if (run === 0) start = ym[j];
        run++;
        if (run >= C.steadyRun) return { value: start, reason: 'Start of the first ' + C.steadyRun + ' months in a row where every cohort has at least ' + minSize + ' first month orders' };
      } else {
        run = month0[j] >= minSize ? 1 : 0;
        start = run ? ym[j] : -1;
      }
      prev = ym[j];
    }
    return { value: null, reason: 'No run of ' + C.steadyRun + ' months in a row where every cohort has at least ' + minSize + ' first month orders' };
  }));

  const legacyCeiling = num(take('legacyCeiling', () => ({ value: GUARD_STRUCTURE.legacyCeiling, reason: 'A closed group of customers cannot grow' }))) ?? GUARD_STRUCTURE.legacyCeiling;
  const offList = scope.off || [];
  const wantLegacy = offList.indexOf('legacy') < 0 && legacyCutoff !== null;
  const folded = cohorts.map((_, j) => wantLegacy && (isPre[j] || (!isNaN(ym[j]) && ym[j] < (legacyCutoff as number))));

  const legacyActual: Array<number | null> = new Array(n).fill(null);
  for (let t = 0; t <= cut && t < n; t++) {
    let sum: number | null = null;
    for (let j = 0; j < J; j++) {
      if (!folded[j] || kinds[j][t] !== 'actual' || !isNum(sizes[j])) continue;
      sum = (sum ?? 0) + (sizes[j] as number) * (rates[j][t] as number);
    }
    legacyActual[t] = sum;
  }

  const credibleOn = offList.indexOf('credible') < 0 && minSize !== null;
  const teaches = cohorts.map((_, j) => !folded[j] && !isPre[j] && birth[j] >= 0 && birth[j] <= cut && (credibleOn ? month0[j] >= (minSize as number) && month0[j] > 0 : true));
  const weightOf = (j: number) => (credibleOn ? month0[j] : sizeOf(j) > 0 ? sizeOf(j) : 1);
  type AgeCell = { j: number; t: number; rate: number; w: number };
  const byAge: AgeCell[][] = [];
  for (let j = 0; j < J; j++) {
    if (!teaches[j]) continue;
    for (let t = birth[j]; t <= cut && t < n; t++) {
      if (kinds[j][t] !== 'actual') continue;
      const a = t - birth[j];
      (byAge[a] = byAge[a] || []).push({ j, t, rate: rates[j][t] as number, w: weightOf(j) });
    }
  }
  byAge.forEach(list => list && list.sort((x, y) => birth[y.j] - birth[x.j]));
  const countAt = (a: number) => (byAge[a] ? byAge[a].length : 0);
  const pooled = (list: AgeCell[]) => {
    let s = 0, w = 0;
    list.forEach(c => { s += c.w * c.rate; w += c.w; });
    return w > 0 ? s / w : null;
  };
  const pooledAt = (a: number) => (byAge[a] ? pooled(byAge[a]) : null);

  const tuned = scope.tuned || {};
  const tunedReason = 'Set by back test' + (isNum(tuned.error) ? ' · error ' + pct(tuned.error) : '');
  const tunedValue = (p: GuardParam, none: string) => () => (isNum((tuned as Record<string, unknown>)[p]) ? { value: (tuned as Record<string, number>)[p], reason: tunedReason } : { value: null, reason: none });
  const bundleWindow = v4 ? SEASON : v3 ? num(take('bundleRetentionWindow', tunedValue('bundleRetentionWindow', 'Needs more months of history for the back test'))) : null;
  let bundle = null as BundleRetention | null;
  let bundleNeeds = false as boolean;
  const legacyRetention = num(take('legacyRetention', () => {
    if (v3 && bundleWindow !== null && wantLegacy) {
      const matched: number[] = [];
      for (let t = SEASON; t <= cut && t < n; t++) {
        if (isNum(legacyActual[t]) && isNum(legacyActual[t - SEASON]) && months[t - SEASON] >= (legacyCutoff as number)) matched.push(t);
      }
      if (matched.length >= bundleWindow) {
        const window = matched.slice(matched.length - bundleWindow);
        let now = 0, before = 0;
        window.forEach(t => { now += legacyActual[t] as number; before += legacyActual[t - SEASON] as number; });
        if (before > 0) {
          const yearly = now / before;
          const raw = Math.pow(yearly, 1 / SEASON);
          const v = Math.min(legacyCeiling, raw);
          bundle = { window: bundleWindow, months: window, now, before, yearly, kept: v };
          return { value: v, reason: 'Measured from the bundle\'s latest ' + bundleWindow + ' months against the same months a year earlier' + (raw > legacyCeiling ? ', held at the ' + pct(legacyCeiling) + ' ceiling' : '') };
        }
      } else {
        bundleNeeds = true;
      }
    }
    const have = legacyActual.filter(isNum).length;
    if (have >= 2 * SEASON) {
      let last = 0, prior = 0;
      for (let t = cut - SEASON + 1; t <= cut; t++) last += legacyActual[t] ?? 0;
      for (let t = cut - 2 * SEASON + 1; t <= cut - SEASON; t++) prior += legacyActual[t] ?? 0;
      if (prior > 0) {
        const raw = Math.pow(last / prior, 1 / SEASON);
        const v = Math.min(legacyCeiling, raw);
        return { value: v, reason: 'Measured from the bundle\'s last ' + SEASON + ' months against the ' + SEASON + ' before' + (raw > legacyCeiling ? ', held at the ' + pct(legacyCeiling) + ' ceiling' : '') };
      }
    }
    const lo = pooledAt(C.matureAgeTo), hi = pooledAt(C.matureAgeTo + SEASON);
    if (isNum(lo) && isNum(hi) && lo > 0) {
      const v = Math.min(legacyCeiling, Math.pow(hi / lo, 1 / SEASON));
      return { value: v, reason: 'Measured from how mature cohorts fade between ages ' + C.matureAgeTo + ' and ' + (C.matureAgeTo + SEASON) };
    }
    return { value: null, reason: 'Not enough bundle history or mature cohorts at age ' + (C.matureAgeTo + SEASON) };
  }));

  const trendWindow = v3 ? GUARD_STRUCTURE.trendWindow : num(take('trendWindow', () => ({ value: GUARD_STRUCTURE.trendWindow, reason: 'A full year on each side cancels seasonal swings' }))) || GUARD_STRUCTURE.trendWindow;
  const bandsRaw = v3 ? GUARD_STRUCTURE.trendBands.slice() : take('trendBands', () => ({ value: GUARD_STRUCTURE.trendBands.slice(), reason: 'First repeat order, early months, first half year, first year, second year, long-standing customers' }));
  const bands = Array.isArray(bandsRaw) && bandsRaw.length ? (bandsRaw as number[]) : GUARD_STRUCTURE.trendBands;
  const bandOf = (a: number) => {
    let b = -1;
    bands.forEach((start, i) => { if (a >= start) b = i; });
    return b;
  };
  const bandAges = (b: number) => {
    const lo = bands[b], hi = b + 1 < bands.length ? bands[b + 1] - 1 : byAge.length - 1;
    const out: number[] = [];
    for (let a = lo; a <= hi; a++) out.push(a);
    return out;
  };
  const trendApplications = v3 ? GUARD_STRUCTURE.trendApplications : num(take('trendApplications', () => ({ value: GUARD_STRUCTURE.trendApplications, reason: 'One year of evidence supports one year of projection' }))) || GUARD_STRUCTURE.trendApplications;

  const limitsRaw = v3 ? bands.flatMap(() => [1, 1]) : take('trendLimits', () => {
    const years: number[] = [];
    const firstYear = Math.floor((legacyCutoff ?? (ordered.length ? ym[ordered[0]] : cutYm)) / 100);
    for (let y = firstYear; y * 100 + 12 <= cutYm; y++) {
      if (!wantLegacy || y * 100 + 1 >= (legacyCutoff as number)) years.push(y);
    }
    const out: number[] = [];
    let anyMove = false, anyMeasured = false;
    const measuredMove = (ages: number[]) => {
      let recent = 0, prior = 0;
      ages.forEach(a => {
        const list = byAge[a] || [];
        const r = list.slice(0, trendWindow), p = list.slice(trendWindow, 2 * trendWindow);
        if (r.length < k || p.length < k) return;
        recent += pooled(r) as number;
        prior += pooled(p) as number;
      });
      return prior > 0 ? Math.abs(recent / prior - 1) : null;
    };
    bands.forEach((_, b) => {
      const ages = bandAges(b);
      const rateOfYear = (y: number) => {
        let s = 0, w = 0;
        ages.forEach(a => (byAge[a] || []).forEach(c => {
          if (Math.floor(ym[c.j] / 100) !== y) return;
          s += c.w * c.rate;
          w += c.w;
        }));
        return w > 0 ? s / w : null;
      };
      const moves: number[] = [];
      for (let i = 1; i < years.length; i++) {
        const prev = rateOfYear(years[i - 1]), cur = rateOfYear(years[i]);
        if (isNum(prev) && isNum(cur) && prev > 0) moves.push(Math.abs(cur / prev - 1));
      }
      const m = median(moves);
      const fallback = m === null && ageFade ? measuredMove(ages) : null;
      if (m !== null) anyMove = true;
      if (fallback !== null) anyMeasured = true;
      const d = Math.min(m ?? fallback ?? 0, 1);
      out.push(1 - d, 1 + d);
    });
    return {
      value: out,
      reason: anyMove
        ? 'Each age group\'s typical year on year move across ' + years.length + ' complete years'
        : anyMeasured
          ? 'Under 2 complete years of history, so each age group uses the trend measured over the last ' + trendWindow + ' months'
          : 'Not enough history to measure a trend, so it is held at no change'
    };
  });
  const limitsOk = Array.isArray(limitsRaw) && limitsRaw.length === bands.length * 2;
  const limits = bands.flatMap((_, b) => [0, 1].map(s => {
    const v = limitsOk ? (limitsRaw as Array<number | null>)[2 * b + s] : null;
    return isNum(v) ? v : 1;
  }));

  const tailWindow = num(take('tailWindow', () => {
    if (!flatTail) return { value: GUARD_STRUCTURE.tailWindow, reason: 'A full seasonal cycle of well-evidenced ages from age ' + C.tailStartAge };
    for (let a = byAge.length - 1; a >= C.tailStartAge; a--) {
      if (countAt(a) >= k) return { value: GUARD_STRUCTURE.tailWindow, reason: 'A full seasonal cycle of well-evidenced ages from age ' + C.tailStartAge };
    }
    return { value: null, reason: 'No well-evidenced age of ' + C.tailStartAge + ' months or over yet' };
  }));

  const monthFactors: number[] = new Array(SEASON).fill(1);
  const monthYears: number[] = new Array(SEASON).fill(0);
  const fadeFactors: number[] = new Array(SEASON).fill(1);
  let factorsMeasured = false;
  if (!flatTail) {
    const sums = new Array(SEASON).fill(0), weights = new Array(SEASON).fill(0), counts = new Array(SEASON).fill(0);
    const byMonth: Record<number, { s: number; w: number; o: number }> = {};
    for (let a = 1; a < byAge.length; a++) {
      const p = pooledAt(a);
      if (!isNum(p) || p <= 0) continue;
      byAge[a].forEach(c => {
        const m = (months[c.t] % 100) - 1;
        sums[m] += c.w * (c.rate / p);
        weights[m] += c.w;
        counts[m]++;
        const at = byMonth[c.t] || (byMonth[c.t] = { s: 0, w: 0, o: 0 });
        at.s += c.w * (c.rate / p);
        at.w += c.w;
        at.o += c.w * c.rate;
      });
    }
    const seen = Object.keys(byMonth).map(Number).sort((x, y) => x - y);
    if (seen.length >= SEASON) {
      const history = sums.map((s, m) => (counts[m] >= k && weights[m] > 0 ? s / weights[m] : 1));
      const historyTotal = history.reduce((s, x) => s + x, 0);
      if (historyTotal > 0) history.forEach((x, m) => { fadeFactors[m] = (x * SEASON) / historyTotal; });
      let raw: number[];
      if (v4) {
        const ratioOf = (t: number) => (byMonth[t] && byMonth[t].w > 0 ? byMonth[t].s / byMonth[t].w : null);
        const effSum = new Array(SEASON).fill(0), effW = new Array(SEASON).fill(0), years = new Array(SEASON).fill(0);
        seen.forEach(t => {
          const own = ratioOf(t);
          let s = 0, c = 0;
          for (let x = t - LOCAL_SPAN; x <= t + LOCAL_SPAN; x++) {
            const r = x === t ? null : ratioOf(x);
            if (r !== null) { s += r; c++; }
          }
          if (own === null || !c || s <= 0 || byMonth[t].o <= 0) return;
          const m = (months[t] % 100) - 1;
          effSum[m] += byMonth[t].o * (own / (s / c));
          effW[m] += byMonth[t].o;
          years[m]++;
        });
        raw = effSum.map((s, m) => (counts[m] >= k && effW[m] > 0 ? s / effW[m] : 1));
        raw.forEach((_, m) => { monthYears[m] = counts[m] >= k && effW[m] > 0 ? years[m] : 0; });
      } else {
        raw = history;
        seen.forEach(t => { const m = (months[t] % 100) - 1; if (counts[m] >= k && weights[m] > 0) monthYears[m]++; });
      }
      const total = raw.reduce((s, x) => s + x, 0);
      if (total > 0) raw.forEach((x, m) => { monthFactors[m] = (x * SEASON) / total; });
      factorsMeasured = true;
    }
  }
  const factorAt = (t: number) => monthFactors[(months[t] % 100) - 1];
  const fadeFactorAt = (t: number) => fadeFactors[(months[t] % 100) - 1];
  const effective = (rule: CohortInputRule): CohortInputRule => (!flatTail && rule.method === 'seasonal' ? Object.assign({ method: 'wavg', n: rule.n, season: true }, v3 ? null : { trend: true }) as CohortInputRule : rule);
  const trendOf = (rule: CohortInputRule) => (flatTail ? rule.method === 'seasonal' : !v3 && !!rule.trend && rule.method !== 'chain' && rule.method !== 'fixed');
  const seasonOf = (rule: CohortInputRule) => !flatTail && !!rule.season && rule.method !== 'fixed';
  const capSeason = v4 && !!channelRule && seasonOf(effective(channelRule)) && factorsMeasured;
  const capRate = (c: AgeCell) => (capSeason ? c.rate / factorAt(c.t) : c.rate);
  const pooledWith = (list: AgeCell[], ds: (t: number) => number) => {
    let s = 0, w = 0;
    list.forEach(c => { s += (c.w * c.rate) / ds(c.t); w += c.w; });
    return w > 0 ? s / w : null;
  };
  const normalAt = (a: number) => (byAge[a] ? (capSeason ? pooledWith(byAge[a], factorAt) : pooled(byAge[a])) : null);

  const capFrom: Array<CapSource | null> = bands.map((_, b) => {
    let top: CapSource | null = null;
    bandAges(b).forEach(a => (byAge[a] || []).forEach(c => { const r = capRate(c); if (top === null || r > top.rate) top = { j: c.j, t: c.t, rate: r }; }));
    return top;
  });
  const topIn = capFrom.map(x => (x ? x.rate : null));
  const headroomRaw = !v3 ? null : take('ceilingHeadroom', () => {
    if (topIn.every(x => x === null)) return { value: null, reason: 'No real repeat rate after the first month yet' };
    return {
      value: bands.map((_, b) => {
        if (topIn[b] === null) return null;
        const rises: number[] = [];
        bandAges(b).forEach(a => {
          const list = (byAge[a] || []).slice().sort((x, y) => birth[x.j] - birth[y.j]);
          for (let i = 1; i < list.length; i++) {
            if (list[i - 1].rate > 0 && list[i].rate > list[i - 1].rate) rises.push(list[i].rate / list[i - 1].rate - 1);
          }
        });
        return median(rises) ?? 0;
      }),
      reason: 'Median rise between consecutive cohorts at the same age in each age group, counting only rises'
    };
  });
  const headroomOf = (b: number) => {
    if (isNum(headroomRaw)) return headroomRaw;
    const v = Array.isArray(headroomRaw) ? headroomRaw[b] : null;
    return isNum(v) ? v : 0;
  };
  const headroom = bands.map((_, b) => headroomOf(b));
  const rateCap = take('rateCap', () => {
    if (topIn.every(x => x === null)) return { value: null, reason: 'No real repeat rate after the first month yet' };
    if (v3) {
      return {
        value: topIn.map((top, b) => (top === null ? null : top * (1 + headroom[b]))),
        reason: 'Highest real rate in each age group' + (capSeason ? ' with seasonality taken out' : '') + ' × (1 + headroom above the best real rate)'
      };
    }
    return {
      value: topIn.map((top, b) => (top === null ? null : top * Math.max(limits[2 * b + 1], 1))),
      reason: 'Highest real rate in each age group × that group\'s largest rise allowed'
    };
  });
  const capOf = (a: number): number | null => {
    if (isNum(rateCap)) return rateCap;
    if (!Array.isArray(rateCap)) return null;
    const b = bandOf(a);
    const v = b >= 0 ? rateCap[b] : null;
    return isNum(v) ? v : null;
  };

  const qaMultiple = num(take('qaMultiple', () => {
    const ratios: number[] = [];
    for (let a = 1; a < byAge.length; a++) {
      const p = normalAt(a);
      if (!isNum(p) || p <= 0) continue;
      byAge[a].forEach(c => ratios.push(capRate(c) / p));
    }
    const v = percentile(ratios, C.qaPercentile);
    if (v === null) return { value: null, reason: 'No real results to measure yet' };
    const r = Math.round(v * 10) / 10;
    return { value: r, reason: 'Set so ' + C.qaPercentile + ' in 100 real results would pass' };
  }));

  const firstForecast = cut + 1 < n ? months[Math.max(0, cut + 1)] : null;
  const retentionValue = v3 ? num(take('retentionChange', () => ({ value: 0, reason: 'The forecast follows the data unless you expect a change' }))) ?? 0 : 0;
  const retentionStart = v3 ? num(take('retentionChangeStart', () => ({ value: firstForecast, reason: 'First forecast month' }))) : null;
  const ownHistoryOn = v4 ? 1 : v3 ? num(take('ownHistoryOn', tunedValue('ownHistoryOn', 'Needs more months of history for the back test'))) : null;
  const ownMinMonths = v4
    ? num(take('ownHistoryMinMonths', () => (isNum(tuned.ownHistoryMinMonths) ? { value: tuned.ownHistoryMinMonths, reason: 'Set from your data' } : { value: null, reason: 'Needs more months of actuals before it can be set from your data' })))
    : v3 ? num(take('ownHistoryMinMonths', tunedValue('ownHistoryMinMonths', 'Needs more months of history for the back test'))) : null;

  const hasHistory = month0.some(m => m > 0);
  const optional = (p: GuardParam) => p === 'retentionChange' || p === 'retentionChangeStart' || p === 'ceilingHeadroom'
    || (p === 'bundleRetentionWindow' && !wantLegacy) || (p === 'ownHistoryMinMonths' && (v4 || ownHistoryOn === 0));
  const needs: GuardParam[] = hasHistory ? GUARD_PARAMS.filter(p => params[p] && params[p].value === null && offList.indexOf(GUARD_OF_PARAM[p]) < 0 && !optional(p)) : [];
  const legacyOn = wantLegacy && legacyRetention !== null;
  if (!legacyOn) folded.fill(false);
  const tailWanted = offList.indexOf('tail') < 0;
  let fade: number | null = null;
  let fadeFrom: 'curve' | 'bundle' | null = null;
  const fadeAges: number[] = [];
  if (!flatTail && tailWanted) {
    let lsum = 0, wsum = 0;
    const span = tailWindow ?? GUARD_STRUCTURE.tailWindow;
    for (let a = byAge.length - 1; a >= Math.max(C.tailStartAge, 1) && fadeAges.length < span; a--) {
      if (countAt(a) < k) continue;
      let now = 0, before = 0, w = 0, pairs = 0;
      byAge[a].forEach(c => {
        const prev = rates[c.j][c.t - 1];
        if (kinds[c.j][c.t - 1] !== 'actual' || !isNum(prev)) return;
        now += c.w * c.rate;
        before += c.w * prev;
        w += c.w;
        pairs++;
      });
      if (pairs < k || now <= 0 || before <= 0) continue;
      lsum += w * Math.log(now / before);
      wsum += w;
      fadeAges.unshift(a);
    }
    const curve = wsum > 0 ? Math.min(1, Math.exp(lsum / wsum)) : null;
    if (curve !== null && (!tailEvidence || fadeAges.length >= span)) {
      fade = curve;
      fadeFrom = 'curve';
    } else if (isNum(legacyRetention)) {
      fade = Math.min(1, legacyRetention);
      fadeFrom = 'bundle';
    } else if (curve !== null) {
      fade = curve;
      fadeFrom = 'curve';
    }
  }
  const fadeCells: CohortCellRef[] = fadeFrom === 'curve' ? fadeAges.flatMap(a => byAge[a].map(c => ({ j: c.j, t: c.t }))) : [];
  const on: Record<GuardId, boolean> = {
    legacy: legacyOn,
    credible: credibleOn,
    anchors: offList.indexOf('anchors') < 0,
    trend: !v3 && offList.indexOf('trend') < 0,
    tail: tailWanted && (flatTail ? tailWindow !== null : fade !== null),
    cap: offList.indexOf('cap') < 0 && rateCap !== null,
    qa: offList.indexOf('qa') < 0 && rateCap !== null && qaMultiple !== null,
    retention: v3 && offList.indexOf('retention') < 0 && retentionValue !== 0 && retentionStart !== null,
    own: v4 ? ownMinMonths !== null : v3 && offList.indexOf('own') < 0 && ownHistoryOn === 1 && ownMinMonths !== null
  };
  const off = GUARD_IDS.reduce((acc, g) => Object.assign(acc, { [g]: offList.indexOf(g) >= 0 }), {} as Record<GuardId, boolean>);

  let tailRate: number | null = null;
  let tailAges: number[] = [];
  if (on.tail && flatTail) {
    for (let a = byAge.length - 1; a >= C.tailStartAge && tailAges.length < (tailWindow as number); a--) {
      if (countAt(a) >= k) tailAges.unshift(a);
    }
    tailRate = tailAges.length ? pooled(tailAges.flatMap(a => byAge[a])) : null;
    if (tailRate === null) on.tail = false;
  }

  const trendCache: Record<number, { measured: number; applied: number; limited: boolean } | null> = {};
  const trendFor = (a: number) => {
    const b = bandOf(a);
    if (b < 0) return null;
    if (trendCache[b] !== undefined) return trendCache[b];
    let recent = 0, prior = 0;
    bandAges(b).forEach(x => {
      const list = byAge[x] || [];
      const r = list.slice(0, trendWindow), p = list.slice(trendWindow, 2 * trendWindow);
      if (r.length < k || p.length < k) return;
      recent += pooled(r) as number;
      prior += pooled(p) as number;
    });
    const measured = prior > 0 ? recent / prior : 1;
    const lo = limits[2 * b], hi = limits[2 * b + 1];
    const clamped = Math.max(lo, Math.min(hi, measured));
    const applied = Math.pow(clamped, trendApplications);
    trendCache[b] = { measured, applied, limited: clamped !== measured };
    return trendCache[b];
  };
  const trendYearOf = () => {
    if (v3) return null;
    let recent = 0, prior = 0;
    for (let a = 1; a < byAge.length; a++) {
      const list = byAge[a] || [];
      const r = list.slice(0, trendWindow), p = list.slice(trendWindow, 2 * trendWindow);
      if (bandOf(a) < 0 || r.length < k || p.length < k) continue;
      recent += pooled(r) as number;
      prior += pooled(p) as number;
    }
    return prior > 0 ? recent / prior - 1 : null;
  };

  const changed = GUARD_IDS.reduce((acc, g) => Object.assign(acc, { [g]: 0 }), {} as Record<GuardId, number>);
  const termOf = (c: AgeCell, withWeight: boolean): CohortSourceTerm => (withWeight ? { j: c.j, t: c.t, rate: c.rate, weight: c.w } : { j: c.j, t: c.t, rate: c.rate });
  const anyRate = (b: number, t: number) => {
    if (b < 0 || t < 0) return null;
    const j = cohorts.findIndex((_, x) => birth[x] === b && !folded[x]);
    return j >= 0 && isNum(rates[j][t]) ? { j, t, rate: rates[j][t] as number } : null;
  };

  type Pick = { value: number; step: GuardedCellSource['step']; terms: CohortSourceTerm[]; curve?: GuardedCellSource['curve']; factor?: GuardedCellSource['factor']; fade?: FadeSource; own?: OwnHistorySource; notes: GuardNote[]; fromRule: boolean };
  type AgeLink = { rate: number | null; cohorts: number; cells: CohortCellRef[] };
  const ageLinks: AgeLink[] = [];
  const ageLinkAt = (a: number): AgeLink => {
    if (ageLinks[a]) return ageLinks[a];
    let now = 0, before = 0, cohortsAt = 0;
    const cells: CohortCellRef[] = [];
    (byAge[a] || []).forEach(c => {
      const prev = rates[c.j][c.t - 1];
      if (kinds[c.j][c.t - 1] !== 'actual' || !isNum(prev)) return;
      now += (c.w * c.rate) / fadeFactorAt(c.t);
      before += (c.w * prev) / fadeFactorAt(c.t - 1);
      cohortsAt++;
      cells.push({ j: c.j, t: c.t }, { j: c.j, t: c.t - 1 });
    });
    return (ageLinks[a] = { rate: now > 0 && before > 0 ? now / before : null, cohorts: cohortsAt, cells });
  };
  const fadeAt = (a: number): FadeAge => {
    const link = ageLinkAt(a);
    const share = link.rate === null ? 0 : Math.min(1, link.cohorts / k);
    const value = Math.min(1, share * (link.rate ?? 0) + (1 - share) * (fade as number));
    return { age: a, rate: link.rate, cohorts: link.cohorts, share, value };
  };
  const fadeCell = (j: number, t: number, list: AgeCell[], notes: GuardNote[]): Pick | null => {
    if (flatTail || !on.tail || fade === null || fadeFrom === null || t - birth[j] < 2) return null;
    for (let x = t - 1; x > birth[j]; x--) {
      const own = rates[j][x];
      if (!isNum(own)) continue;
      const months = t - x;
      let kept = Math.pow(fade, months);
      let age: FadeAge | undefined;
      const cells = fadeCells.slice();
      if (ageFade) {
        kept = 1;
        for (let y = x + 1; y <= t; y++) {
          const step = fadeAt(y - birth[j]);
          kept *= step.value;
          if (step.share > 0) cells.push(...ageLinkAt(y - birth[j]).cells);
          age = step;
        }
      }
      const perMonth = months > 1 ? Math.pow(kept, 1 / months) : kept;
      const value = own * kept;
      notes.push({ guard: 'tail', before: own, after: value, detail: list.length + ' well-sized cohorts at age ' + (t - birth[j]) + ', fewer than ' + k + '. Continued from ' + pct(own) + ' × ' + pct(perMonth) + ' kept each month' + (months > 1 ? ' for ' + months + ' months' : '') });
      const source: FadeSource = Object.assign(
        { value: perMonth, months, from: fadeFrom, ages: fadeAges.slice(), cells },
        ageFade ? { pooled: fade, age } : null
      );
      return { value, step: 'tail', terms: [{ j, t: x, rate: own }], fade: source, notes, fromRule: false };
    }
    return null;
  };
  const takesOut = (rule: CohortInputRule) => v4 && factorsMeasured && seasonOf(rule);
  const pickFor = (j: number, t: number, rule: CohortInputRule): Pick | null => {
    const b = birth[j], a = t - b;
    const list = byAge[a] || [];
    const notes: GuardNote[] = [];
    const ds = takesOut(rule) ? factorAt : () => 1;
    if (!flatTail && rule.method !== 'fixed' && countAt(a) < k) {
      const faded = fadeCell(j, t, list, notes);
      if (faded) return faded;
    }
    switch (rule.method) {
      case 'fixed':
        return isNum(rule.value) ? { value: rule.value, step: 'fixed', terms: [], notes, fromRule: true } : null;
      case 'seasonal': {
        if (on.anchors) {
          const anchor = list.find(c => (b - birth[c.j]) % SEASON === 0);
          const skipped = anyRate(b - SEASON, t - SEASON);
          if (skipped && (!anchor || anchor.j !== skipped.j)) notes.push({ guard: 'anchors', detail: 'Skipped ' + ymText(months[b - SEASON]) + (anchor ? '; used ' + ymText(ym[anchor.j]) + ' actual ' + pct(anchor.rate) : '') });
          if (anchor) return { value: anchor.rate / ds(anchor.t), step: 'seasonal', terms: [termOf(anchor, false)], notes, fromRule: true };
          break;
        }
        const base = anyRate(b - SEASON, t - SEASON);
        if (base) return { value: base.rate / ds(base.t), step: 'seasonal', terms: [base], notes, fromRule: true };
        break;
      }
      case 'prev': {
        const back = rule.n || 1;
        if (on.anchors) {
          const src = list.find(c => birth[c.j] === b - back);
          if (src) return { value: src.rate / ds(src.t), step: 'prev', terms: [termOf(src, false)], notes, fromRule: true };
          break;
        }
        const base = anyRate(b - back, t - back);
        if (base) return { value: base.rate / ds(base.t), step: 'prev', terms: [base], notes, fromRule: true };
        break;
      }
      case 'avg':
      case 'wavg': {
        if (!on.anchors) {
          const terms: CohortSourceTerm[] = [];
          for (let x = 1; x <= (rule.n || 1); x++) {
            const p = anyRate(b - x, t - x);
            if (p) terms.push(rule.method === 'wavg' ? Object.assign(p, { weight: weightOf(p.j) }) : p);
          }
          if (!terms.length) break;
          const w = terms.reduce((s, p) => s + (p.weight ?? 1), 0);
          const v = rule.method === 'avg' ? terms.reduce((s, p) => s + p.rate / ds(p.t), 0) / terms.length : terms.reduce((s, p) => s + ((p.weight as number) * p.rate) / ds(p.t), 0) / w;
          return { value: v, step: 'average', terms, notes, fromRule: true };
        }
        const take2 = list.slice(0, rule.n || k);
        if (take2.length >= Math.min(k, rule.n || k)) {
          const v = rule.method === 'avg' ? take2.reduce((s, c) => s + c.rate / ds(c.t), 0) / take2.length : (pooledWith(take2, ds) as number);
          return { value: v, step: 'average', terms: take2.map(c => termOf(c, rule.method === 'wavg')), notes, fromRule: true };
        }
        break;
      }
      case 'chain': {
        if (!flatTail && a === 1) {
          const recent = list.slice(0, rule.n || k);
          if (recent.length >= Math.min(k, rule.n || k)) return { value: pooledWith(recent, ds) as number, step: 'start', terms: recent.map(c => termOf(c, true)), notes, fromRule: true };
          break;
        }
        const own = rates[j][t - 1];
        if (!isNum(own)) break;
        let num2 = 0, den2 = 0, used = 0;
        const cells: CohortCellRef[] = [];
        for (const c of list) {
          if (used >= (rule.n || 1)) break;
          const prevRate = rates[c.j][c.t - 1];
          if (kinds[c.j][c.t - 1] !== 'actual' || !isNum(prevRate)) continue;
          num2 += c.rate;
          den2 += prevRate;
          cells.push({ j: c.j, t: c.t }, { j: c.j, t: c.t - 1 });
          used++;
        }
        let factor = den2 > 0 ? { value: num2 / den2, num: num2, den: den2, cohorts: used, fromCurve: false, cells } : null;
        if (!factor) {
          const hi = pooledAt(a), lo = pooledAt(a - 1);
          if (isNum(hi) && isNum(lo) && lo > 0) factor = { value: hi / lo, num: hi, den: lo, cohorts: 0, fromCurve: true, cells: [] };
        }
        if (!factor && !flatTail) {
          const faded = fadeCell(j, t, list, notes);
          if (faded) return faded;
        }
        return { value: own * (factor ? factor.value : 1), step: 'chain', terms: [{ j, t: t - 1, rate: own }], factor, notes, fromRule: true };
      }
      default:
        break;
    }
    if (list.length >= k) {
      const take3 = list.slice(0, k);
      return { value: pooledWith(take3, ds) as number, step: 'average', terms: take3.map(c => termOf(c, true)), notes, fromRule: false };
    }
    if (flatTail && on.tail && a >= C.tailStartAge && isNum(tailRate)) {
      notes.push({ guard: 'tail', param: 'tailWindow', value: tailWindow, after: tailRate, detail: list.length + ' well-sized cohorts at age ' + a + ', fewer than ' + k + '. Held ' + pct(tailRate) + ', the average over ages ' + tailAges[0] + ' to ' + tailAges[tailAges.length - 1] });
      return { value: tailRate, step: 'tail', terms: [], curve: { age: tailAges[tailAges.length - 1], cohorts: tailAges.reduce((s, x) => s + countAt(x), 0), cells: tailAges.flatMap(x => byAge[x].map(c => ({ j: c.j, t: c.t }))) }, notes, fromRule: false };
    }
    if (list.length) return { value: pooledWith(list, ds) as number, step: 'pooled', terms: list.map(c => termOf(c, true)), notes, fromRule: false };
    for (let x = Math.min(a, byAge.length - 1); x >= 0; x--) {
      if (!countAt(x)) continue;
      return { value: pooledWith(byAge[x], ds) as number, step: 'curve', terms: [], curve: { age: x, cohorts: countAt(x), cells: byAge[x].map(c => ({ j: c.j, t: c.t })) }, notes, fromRule: false };
    }
    return null;
  };

  type Valued = { pick: Pick; base: number; value: number; season: GuardedCellSource['season'] | null; trend: GuardedCellSource['trend']; limited: { before: number; measured: number; applied: number } | null };
  const valueOf = (j: number, t: number, rule: CohortInputRule): Valued | null => {
    const pick = pickFor(j, t, rule);
    if (!pick) return null;
    const base = Math.max(0, pick.value);
    let value = base;
    let season: GuardedCellSource['season'] | null = null;
    let trend: GuardedCellSource['trend'] = null;
    let limited: Valued['limited'] = null;
    const faded = pick.step === 'tail';
    if (seasonOf(rule) && (!faded || ageFade)) {
      const from = faded ? pick.terms[0].t : t - 1;
      const ratio = faded || pick.step === 'chain';
      const f = ratio ? factorAt(t) / factorAt(from) : factorAt(t);
      season = Object.assign({ value: f, month: months[t] % 100, ratio, measured: factorsMeasured }, faded ? { from: months[from] % 100 } : null, !ratio && takesOut(rule) ? { takenOut: true } : null);
      value *= f;
    }
    if (trendOf(rule) && on.trend && (flatTail || pick.step !== 'tail')) {
      const tr = trendFor(t - birth[j]);
      if (tr && tr.applied !== 1) {
        const before = value;
        value *= tr.applied;
        trend = { value: tr.applied, num: tr.measured, den: 1, pairs: 0, capped: tr.limited, cells: [] };
        if (tr.limited) limited = { before, measured: tr.measured, applied: tr.applied };
      }
    }
    return { pick, base, value, season, trend, limited };
  };

  const methods: Array<CohortMethodUse | null> = cohorts.map((c, j) => {
    if (isPre[j] || folded[j] || birth[j] < 0) return null;
    return input.cohortSet && input.cohortSet(String(c.label)) ? 'user' : 'tab';
  });
  const ownSeason = v3 && seasonOf(effective(channelRule));
  const deseason = (t: number) => (ownSeason ? factorAt(t) : 1);
  type Decay = OwnHistorySource['decays'][number];
  const decays: Decay[] = [];
  const decayAt = (a: number): Decay => {
    if (decays[a]) return decays[a];
    let now = 0, before = 0, used = 0;
    for (const c of byAge[a + 1] || []) {
      if (used >= k) break;
      const prev = rates[c.j][c.t - 1];
      if (kinds[c.j][c.t - 1] !== 'actual' || !isNum(prev)) continue;
      now += (c.w * c.rate) / deseason(c.t);
      before += (c.w * prev) / deseason(c.t - 1);
      used++;
    }
    const measured = before > 0;
    return (decays[a] = { age: a, value: measured ? now / before : isNum(fade) ? fade : 1, cohorts: measured ? used : 0, fromFade: !measured });
  };
  type OwnBase = { age: number; anchor: number; months: OwnHistorySource['anchorMonths'] };
  const ownBaseOf = (j: number): OwnBase | null => {
    let A = -1, count = 0;
    for (let t = birth[j] + 1; t <= cut && t < n; t++) if (kinds[j][t] === 'actual') { A = t - birth[j]; count++; }
    if (A < 1 || ownMinMonths === null || count < ownMinMonths) return null;
    const list: OwnHistorySource['anchorMonths'] = [];
    for (let x = A; x >= 1 && list.length < k; x--) {
      const t = birth[j] + x;
      if (kinds[j][t] !== 'actual') continue;
      const factor = deseason(t);
      let level = (rates[j][t] as number) / factor;
      for (let y = x; y < A; y++) level *= decayAt(y).value;
      list.push({ t, rate: rates[j][t] as number, factor, level });
    }
    return { age: A, anchor: list.reduce((s, m) => s + m.level, 0) / list.length, months: list };
  };
  const ownBases: Array<OwnBase | null> = cohorts.map((_, j) => (on.own && methods[j] === 'tab' && teaches[j] && month0[j] > 0 && birth[j] < cut ? ownBaseOf(j) : null));
  ownBases.forEach((b, j) => { if (b) methods[j] = 'own'; });
  const ownValued = (j: number, t: number): Valued => {
    const b = ownBases[j] as OwnBase;
    const steps: Decay[] = [];
    let base = b.anchor;
    for (let y = b.age; y < t - birth[j]; y++) {
      const d = decayAt(y);
      base *= d.value;
      steps.push(d);
    }
    const f = deseason(t);
    const own: OwnHistorySource = { age: b.age, anchor: b.anchor, anchorMonths: b.months, decays: steps, factor: f };
    const pick: Pick = { value: base, step: 'own', terms: b.months.map(m => ({ j, t: m.t, rate: m.rate })), own, notes: [], fromRule: true };
    const season = ownSeason ? { value: f, month: months[t] % 100, ratio: false, measured: factorsMeasured } : null;
    return { pick, base: Math.max(0, base), value: Math.max(0, base) * f, season, trend: null, limited: null };
  };
  const valuedAt = (j: number, t: number, rule: CohortInputRule) => (ownBases[j] ? ownValued(j, t) : valueOf(j, t, rule));

  const pure: Array<Array<number | null>> = cohorts.map(() => new Array(n).fill(null));
  const capScale: number[][] | null = capSeason ? cohorts.map(() => new Array(n).fill(1)) : null;
  const capSlack = v4 ? 1e-12 : 0;
  const lifted: Array<{ j: number; t: number; value: number }> = [];
  const typedFrom = cohorts.map((_, j) => {
    for (let t = cut + 1; t < n; t++) if (kinds[j][t] === 'override') return t;
    return -1;
  });
  let trendUsed = !!channelRule && trendOf(effective(channelRule));
  let seasonUsed = !!channelRule && seasonOf(effective(channelRule));
  for (let t = Math.max(0, cut + 1); t < n; t++) {
    for (let j = 0; j < J; j++) {
      if (kinds[j][t] !== null || folded[j]) continue;
      if (isPre[j]) {
        for (let x = t - 1; x >= 0; x--) {
          if (!isNum(rates[j][x])) continue;
          rates[j][t] = rates[j][x];
          kinds[j][t] = 'rule';
          if (sources) sources[j][t] = { method: 'carry', terms: [{ j, t: x, rate: rates[j][x] as number }], result: rates[j][t] as number };
          break;
        }
        continue;
      }
      if (birth[j] < 0 || t <= birth[j]) continue;
      const rule = ruleFor(String(cohorts[j].label));
      const eff = effective(rule);
      const ownUse = !!ownBases[j];
      if (!ownUse && trendOf(eff)) trendUsed = true;
      if (ownUse ? ownSeason : seasonOf(eff)) seasonUsed = true;
      const valued = valuedAt(j, t, eff);
      if (!valued) continue;
      const { pick, trend: trendInfo, season: seasonInfo } = valued;
      let v = valued.value;
      pure[j][t] = v;
      const notes = pick.notes;
      if (notes.some(x => x.guard === 'anchors')) changed.anchors++;
      if (pick.step === 'tail') changed.tail++;
      if (valued.limited) {
        changed.trend++;
        const band = bandOf(t - birth[j]);
        notes.push({ guard: 'trend', param: 'trendLimits', value: [limits[2 * band], limits[2 * band + 1]], before: valued.limited.before, after: v, detail: 'Measured ' + valued.limited.measured.toFixed(2) + ', limited to ' + valued.limited.applied.toFixed(2) });
      }
      let carry: GuardedCellSource['carry'] | null = null;
      const own = rates[j][t - 1];
      if (!flatTail && typedFrom[j] >= 0 && typedFrom[j] < t && t - 1 > cut && pick.step !== 'tail' && pick.step !== 'chain' && isNum(own)) {
        const before = kinds[j][t - 1] === 'override' ? (valuedAt(j, t - 1, eff) || { value: null }).value : pure[j][t - 1];
        if (isNum(before) && before > 0) {
          carry = { j, t: t - 1, rate: own, before, now: v };
          v = (own * v) / before;
        }
      }
      let change: GuardedCellSource['change'] | null = null;
      const unchanged = v;
      if (on.retention && months[t] >= (retentionStart as number)) {
        change = { value: retentionValue, start: retentionStart as number, before: v };
        v *= 1 + retentionValue;
        changed.retention++;
      }
      const g = capSeason && seasonInfo ? factorAt(t) : 1;
      if (capScale) capScale[j][t] = g;
      const raw = on.cap && t - birth[j] >= 1 ? capOf(t - birth[j]) : null;
      const cap = raw === null ? null : raw * g;
      let capInfo: GuardedCellSource['cap'] | null = null;
      if (cap !== null && v > cap + capSlack) {
        const band = bandOf(t - birth[j]);
        const from = v3 && band >= 0 ? capFrom[band] : null;
        if (from) capInfo = Object.assign({ best: from.rate, j: from.j, t: from.t, headroom: headroom[band], uncapped: v }, capSeason ? { factor: g } : null);
        notes.push(Object.assign(
          { guard: 'cap' as GuardId, param: 'rateCap' as GuardParam, value: cap, before: v, after: cap },
          from ? { detail: 'Best real rate ' + pct(from.rate) + ' (' + ymText(ym[from.j]) + ' cohort in ' + ymText(months[from.t]) + ')' + (capSeason ? ' with seasonality taken out' : '') + ' × (1 + ' + pct(headroom[band]) + ' headroom)' + (capSeason && g !== 1 ? ' × ' + g.toFixed(2) + ' ' + ymText(months[t]).split(' ')[0] + ' factor' : '') } : null
        ));
        changed.cap++;
        v = cap;
      }
      if (change) lifted.push({ j, t, value: v });
      rates[j][t] = change ? (cap !== null && unchanged > cap + capSlack ? cap : unchanged) : v;
      kinds[j][t] = pick.fromRule ? 'rule' : 'fallback';
      if (ownUse) changed.own++;
      if (sources) {
        sources[j][t] = Object.assign(
          { method: (ownUse ? 'own' : pick.fromRule ? eff.method : 'fallback') as CohortCellSource['method'], rule, terms: pick.terms, result: v, step: pick.step, guards: notes },
          pick.curve ? { curve: pick.curve } : null,
          pick.factor !== undefined ? { factor: pick.factor } : null,
          pick.fade ? { fade: pick.fade } : null,
          pick.own ? { own: pick.own } : null,
          trendInfo ? { trend: trendInfo } : null,
          !flatTail && (seasonInfo || trendInfo || carry || change) ? { base: valued.base } : null,
          seasonInfo ? { season: seasonInfo } : null,
          carry ? { carry } : null,
          change ? { change } : null,
          capInfo ? { cap: capInfo } : null
        );
      }
    }
  }

  lifted.forEach(x => { rates[x.j][x.t] = x.value; });
  for (let j = 0; j < J; j++) if (folded[j]) changed.legacy++;
  if (credibleOn) for (let j = 0; j < J; j++) if (!folded[j] && !isPre[j] && birth[j] >= 0 && birth[j] <= cut && !teaches[j] && month0[j] > 0) changed.credible++;

  const legacyOrders: Array<number | null> = new Array(n).fill(null);
  const legacySources: Array<LegacySource | null> = new Array(n).fill(null);
  if (on.legacy) {
    const r = legacyRetention as number;
    const have = legacyActual.map((v, t) => (isNum(v) ? t : -1)).filter(t => t >= 0);
    for (let t = 0; t < n; t++) {
      if (t <= cut) {
        legacyOrders[t] = legacyActual[t];
        if (isNum(legacyActual[t])) legacySources[t] = { method: 'actual', from: t, base: legacyActual[t] as number, retention: r, power: 0, result: legacyActual[t] as number };
        continue;
      }
      let from = -1;
      for (let x = t - SEASON; x >= 0; x -= SEASON) if (x <= cut && isNum(legacyActual[x])) { from = x; break; }
      const lift = on.retention && months[t] >= (retentionStart as number) ? retentionValue : null;
      if (from >= 0 && have.length >= SEASON) {
        const v = (legacyActual[from] as number) * Math.pow(r, t - from) * (lift === null ? 1 : 1 + lift);
        legacyOrders[t] = v;
        legacySources[t] = Object.assign({ method: 'seasonal' as const, from, base: legacyActual[from] as number, retention: r, power: t - from, result: v }, lift === null ? null : { change: lift });
      } else if (have.length) {
        const avg = have.reduce((s, x) => s + (legacyActual[x] as number), 0) / have.length;
        const v = avg * Math.pow(r, t - cut) * (lift === null ? 1 : 1 + lift);
        legacyOrders[t] = v;
        legacySources[t] = Object.assign({ method: 'average' as const, from: null, base: avg, retention: r, power: t - cut, result: v }, lift === null ? null : { change: lift });
      }
    }
  }

  const flags: Array<Array<QaFlag[] | null>> = cohorts.map(() => new Array(n).fill(null));
  let flagCount = 0;
  if (on.qa) {
    const mult = qaMultiple as number;
    for (let j = 0; j < J; j++) {
      if (folded[j] || isPre[j] || birth[j] < 0) continue;
      for (let t = Math.max(birth[j] + 1, cut + 1); t < n; t++) {
        const v = rates[j][t];
        if (!isNum(v)) continue;
        const a = t - birth[j];
        const g = !capScale ? 1 : kinds[j][t] === 'override' ? factorAt(t) : capScale[j][t];
        const raw = capOf(a);
        const cap = raw === null ? null : raw * g;
        const src = sources ? sources[j][t] : null;
        const tail = src && src.step === 'tail';
        const normal = capSeason ? normalAt(a) : pooledAt(a);
        const expected = !flatTail ? (normal === null ? null : normal * g) : tail ? tailRate : pooledAt(a) ?? (on.tail && a >= C.tailStartAge ? tailRate : null);
        const out: QaFlag[] = [];
        if (cap !== null && v > cap + 1e-12) out.push({ rule: 'R1', threshold: cap, expected });
        if (kinds[j][t] !== 'override' && isNum(expected) && expected > 0 && v > mult * expected + 1e-12) out.push({ rule: 'R2', threshold: mult * expected, expected });
        if (cap !== null && src && src.guards && src.guards.some(g => g.guard === 'cap')) out.push({ rule: 'R3', threshold: cap, expected });
        if (out.length) { flags[j][t] = out; flagCount++; }
      }
    }
  }

  const signal: RetentionSignal[] = !v3 ? [] : bands.map((_, b) => {
    let now = 0, before = 0, pairs = 0;
    bandAges(b).forEach(a => {
      const list = byAge[a] || [];
      list.slice(0, SEASON).forEach(c => {
        const prior = list.find(x => birth[x.j] === birth[c.j] - SEASON);
        if (!prior) return;
        now += c.w * c.rate;
        before += prior.w * prior.rate;
        pairs++;
      });
    });
    return { change: before > 0 ? now / before - 1 : null, pairs };
  });

  const legacySize = folded.reduce((s, f, j) => (f ? s + month0[j] : s), 0);
  const result: GuardedForecastResult = {
    rates,
    kinds,
    guards: {
      on, off, needs, params,
      trendUsed: on.trend && trendUsed,
      seasonUsed,
      monthFactors: monthFactors.slice(),
      monthYears: monthYears.slice(),
      factorsMeasured,
      v4,
      capSeason,
      trendYear: trendYearOf(),
      trendHeld: v3 || bands.every(start => {
        const tr = trendFor(start);
        return !tr || tr.applied === 1;
      }),
      bands: bands.slice(),
      limits,
      caps: bands.map(start => capOf(start)),
      flatTail,
      tailRate: on.tail && flatTail ? tailRate : null,
      tailAges: on.tail && flatTail ? tailAges.slice() : [],
      fade: on.tail && !flatTail ? fade : null,
      fadeFrom: on.tail && !flatTail ? fadeFrom : null,
      fadeAges: on.tail && fadeFrom === 'curve' ? fadeAges.slice() : [],
      fadeNeeds: !flatTail && tailWanted && hasHistory && fade === null,
      v3,
      retention: { value: v3 ? retentionValue : 0, start: retentionStart },
      signal,
      ownOn: on.own,
      ownMinMonths,
      ownChangedAt: v4 && typeof tuned.changedAt === 'string' ? tuned.changedAt : null,
      headroom: v3 ? headroom : [],
      capFrom: v3 ? capFrom : [],
      bundle: on.legacy ? bundle : null,
      bundleNeeds: on.legacy && bundleNeeds,
      tunedError: v3 && isNum(tuned.error) ? tuned.error : null
    },
    legacy: { on: on.legacy, cutoff: on.legacy ? legacyCutoff : null, size: legacySize, orders: legacyOrders, folded, sources: legacySources },
    flags,
    flagCount,
    changed,
    methods
  };
  if (sources) result.sources = sources;
  return result;
}

export function frozenGuardValues(resolved: ResolvedGuards): Partial<Record<GuardParam, GuardParamValue>> {
  const out: Partial<Record<GuardParam, GuardParamValue>> = {};
  GUARD_PARAMS.forEach(p => {
    const info = resolved.params[p];
    if (!info || (info.source === 'user' && p !== 'trendLimits')) return;
    const v = info.source === 'system' ? info.value : info.system;
    out[p] = Array.isArray(v) ? v.slice() : v;
  });
  return out;
}
